import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ChatbotRequest {
  message: string;
  sessionId: string;
  userId: string;
}

export type ChatbotPatientRequest = Omit<ChatbotRequest, 'userId'>;

export interface ChatbotMatchedImage {
  faqId: string;
  imagePath: string;
  imageUrl: string;
  imageCaption: string;
  linkUrl: string;
}

export interface ChatbotMetadata {
  intent?: string;
  retrievalUsed?: boolean;
  matchedFaqIds?: string[];
  matchedImages?: ChatbotMatchedImage[];
  modelSource?: string;
}

export interface ChatbotResponse {
  sessionId: string;
  response: string;
  timestamp: string;
  platform: string;
  metadata?: ChatbotMetadata | null;
}

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private readonly http = inject(HttpClient);

  // Patient requests must go out without the Authorization header, so they use a
  // dedicated HttpClient built on HttpBackend to bypass the API interceptor.
  private readonly rawHttp = new HttpClient(inject(HttpBackend));

  // Shared demo patient identity used for every patient conversation until the
  // mobile app provides a real authenticated patient context.
  private static readonly PATIENT_USER_ID = 'e14238f3-9cfe-4e0d-9978-a8b2f00d8cc0';

  private get baseUrl(): string {
    return environment.chatbotApiUrl.replace(/\/+$/, '');
  }

  /**
   * Sends a staff message to the chatbot backend.
   *
   * The Authorization token is attached automatically by the global
   * API interceptor, so it does not need to be set here.
   */
  sendStaffMessage(request: ChatbotRequest): Observable<ChatbotResponse> {
    return this.http.post<ChatbotResponse>(`${this.baseUrl}/chatbot/staff/message`, request);
  }

  /**
   * Sends a patient message to the chatbot backend.
   *
   * Patient conversations are unauthenticated: the request bypasses the API
   * interceptor (no Authorization header) and uses a fixed demo patient id.
   */
  sendPatientMessage(request: ChatbotPatientRequest): Observable<ChatbotResponse> {
    return this.rawHttp.post<ChatbotResponse>(`${this.baseUrl}/chatbot/patient/message`, {
      ...request,
      userId: ChatbotService.PATIENT_USER_ID,
    });
  }
}
